import React, { useMemo, useState } from "react";
import { router, usePage } from "@inertiajs/react";
import DashboardLayout from "../../layouts/DashboardLayout";

// The runtime kill switch. Every flag here changes patient-facing behaviour the moment it is
// flipped — no restart, no deploy — so this page is built to make the CURRENT state obvious and the
// consequence of a change hard to misread.
//
// "Source" is the column that matters: `env` means the flag is running its deploy-time default,
// `db` means a human has deliberately overridden it. Reset returns a flag to its default rather
// than making someone remember what that default was.
export default function Flags({ flags, counts }) {
  const { flash } = usePage().props;
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState(null);

  const shown = useMemo(() => {
    const q = filter.trim().toUpperCase();
    return q ? flags.filter((f) => f.name.includes(q)) : flags;
  }, [flags, filter]);

  const toggle = (f) => {
    if (f.boot_only) return;
    const next = !f.effective;
    const verb = next ? "Turn ON" : "Turn OFF";
    if (!window.confirm(verb + " " + f.name + "?\n\nThis changes live behaviour immediately.")) return;
    setBusy(f.name);
    router.patch("/settings/flags/" + f.name, { enabled: next }, { onFinish: () => setBusy(null) });
  };

  const reset = (f) => {
    if (!window.confirm("Reset " + f.name + " to its deploy default (" + (f.env ? "ON" : "OFF") + ")?")) return;
    setBusy(f.name);
    router.patch("/settings/flags/" + f.name, { reset: 1 }, { onFinish: () => setBusy(null) });
  };

  return (
    <DashboardLayout title="Feature flags">
      <div className="max-w-5xl mx-auto px-4 py-6">
        <header className="mb-5">
          <h1 className="text-2xl font-semibold text-slate-800">Feature flags</h1>
          <p className="mt-1 text-sm text-slate-500 max-w-2xl">
            Turn a behaviour off the moment it misbehaves — changes take effect immediately, with no
            restart and no deploy. A flag on <span className="font-medium">env</span> is running its
            deploy-time default; <span className="font-medium">db</span> means someone has overridden it.
          </p>
          <div className="mt-3 flex flex-wrap gap-4 text-xs text-slate-500">
            <span>{counts.total} flags</span>
            <span>{counts.overridden} overridden</span>
            <span>{counts.boot_only} boot-only (locked)</span>
          </div>
        </header>

        {flash?.notice && (
          <div className="mb-4 rounded border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-800">
            {flash.notice}
          </div>
        )}
        {flash?.alert && (
          <div className="mb-4 rounded border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-800">
            {flash.alert}
          </div>
        )}

        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter flags…"
          className="mb-4 w-full sm:w-80 rounded border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
        />

        <div className="overflow-x-auto rounded border border-slate-200 bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2 font-medium">Flag</th>
                <th className="px-4 py-2 font-medium">State</th>
                <th className="px-4 py-2 font-medium">Source</th>
                <th className="px-4 py-2 font-medium">Last changed</th>
                <th className="px-4 py-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((f) => (
                <tr key={f.name} className="border-t border-slate-100 hover:bg-slate-50/60">
                  <td className="px-4 py-2 font-mono text-xs text-slate-700">
                    {f.name}
                    {f.boot_only && (
                      <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] uppercase text-slate-500">
                        boot-only
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <span
                      className={
                        "inline-block rounded px-2 py-0.5 text-xs font-medium " +
                        (f.effective ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600")
                      }
                    >
                      {f.effective ? "ON" : "OFF"}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    <span className={"text-xs " + (f.source === "db" ? "font-medium text-amber-700" : "text-slate-400")}>
                      {f.source}
                      {f.source === "db" && f.env !== f.effective && (
                        <span className="ml-1 text-slate-400">(env: {f.env ? "on" : "off"})</span>
                      )}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-500">
                    {f.updated_at ? (
                      <>
                        {f.updated_at}
                        {f.updated_by && <div className="text-slate-400">{f.updated_by}</div>}
                      </>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    <button
                      onClick={() => toggle(f)}
                      disabled={f.boot_only || busy === f.name}
                      title={f.boot_only ? "Read at boot — a database row would be ignored" : ""}
                      className={
                        "rounded px-3 py-1 text-xs font-medium " +
                        (f.boot_only
                          ? "cursor-not-allowed bg-slate-100 text-slate-400"
                          : f.effective
                          ? "bg-rose-600 text-white hover:bg-rose-700"
                          : "bg-emerald-600 text-white hover:bg-emerald-700")
                      }
                    >
                      {busy === f.name ? "…" : f.effective ? "Turn off" : "Turn on"}
                    </button>
                    {f.source === "db" && (
                      <button
                        onClick={() => reset(f)}
                        disabled={busy === f.name}
                        className="ml-2 rounded border border-slate-300 px-3 py-1 text-xs text-slate-600 hover:bg-slate-100"
                      >
                        Reset
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="mt-4 text-xs text-slate-400">
          Boot-only flags are read before the database is available, so an override here would be
          silently ignored — they are locked rather than offered.
        </p>
      </div>
    </DashboardLayout>
  );
}
